/**
 * Màn Chi tiết bài — nơi Lynust thật sự **quyết**.
 *
 * Bốn thứ ở đây không phải trang trí:
 *
 * 1. **Lý do từ chối bắt buộc ≥ `DAI_LY_DO_TOI_THIEU` ký tự**, chặn ngay tại chỗ. Backend đã chặn;
 *    chặn lần nữa ở đây để anh biết trước khi bấm, thay vì bấm rồi ăn 400. Ngưỡng **import từ
 *    `@autoime/shared`**, không chép tay — hai bên lệch nhau là nút bật lên rồi ăn 400 (`B10`).
 * 2. **Nội dung hiện HTML THÔ, không render.** Render đẹp thì `**đậm**` trông như chữ thường và
 *    **không ai thấy Markdown đã lọt** (`AP-88`). Màn duyệt tồn tại để bắt lỗi, không để ngắm.
 * 3. **Kết quả thao tác hiện đầy đủ** — số job bị huỷ và khoá job viết lại. Đó là bằng chứng
 *    `AP-95` đã chạy; im lặng thì không ai biết có huỷ hay không.
 * 4. **Hai nút từ chối tách hẳn.** Từ chối *ảnh* chỉ vẽ lại ảnh; từ chối *bài* viết lại từ đầu và
 *    tốn thêm một lượt gọi model. Gộp thành một nút là để người bấm tự đoán.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { DAI_LY_DO_TOI_THIEU } from "@autoime/shared";

import { api, LoiApi, type KetQuaDuyet } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import { nhanTrangThai } from "../lib/nhan.ts";
import {
  Bao,
  Chip,
  DangTai,
  DauThe,
  DauTrang,
  ViSao,
  ThanhHanhDong,
  Nut,
  Rong,
  The,
  Truong,
  lopO,
} from "../thanh-phan/giao-dien.tsx";
import { useToast } from "../thanh-phan/toast.tsx";

// `DAI_LY_DO_TOI_THIEU` nay IMPORT tu `@autoime/shared` (xem dau file) — truoc 10/09/2026 no la
// mot hang so CHEP TAY o day, va lech mot ben thi o nhap dem theo mot nguong con API chan theo
// nguong khac: nut bat len roi nguoi dung an 400. Xem `checklist.md` **B10**.

/** Thẻ gợi ý — thứ làm cho việc "gộp luật trùng ý" ở vòng học sau này có cơ sở. */
const THE_GOI_Y = [
  "bia-so-lieu",
  "thieu-nguon",
  "sai-chuyen-mon",
  "mui-ai",
  "lech-chu-de",
  "anh-sai-san-pham",
];

export function ChiTietBai() {
  const { id = "" } = useParams();
  const qc = useQueryClient();

  // `GET /bai-viet/:id` — trả bài **bất kể trạng thái**. Trước 09/09/2026 chỗ này tải cả hàng chờ
  // rồi lọc, nên mọi bài đã rời hàng chờ (đã duyệt, đã đăng, bị từ chối, FAILED) đều không mở
  // được: màn hình nói "bài này không còn trong hàng chờ" — đúng và vô dụng. Xem `checklist.md`
  // **B4**.
  const { data: b, isPending, error } = useQuery({
    queryKey: khoa.motBai(id),
    queryFn: () => api.motBai(id),
    enabled: id !== "",
    // 404 là câu trả lời cuối cùng, không phải sự cố tạm thời — thử lại chỉ làm người dùng chờ.
    // So MÃ chứ không dò chuỗi: `LoiApi` mang sẵn `ma`, còn thông điệp là câu tiếng Việt đổi lúc
    // nào cũng được, và lúc nó đổi thì không ai nhớ có một mẫu regex đang bám vào nó.
    retry: (soLan, loi: Error) => !(loi instanceof LoiApi && loi.ma === 404) && soLan < 2,
  });

  const toast = useToast();
  const [lyDo, datLyDo] = useState("");
  const [the, datThe] = useState<string[]>([]);
  const [ketQua, datKetQua] = useState<KetQuaDuyet | null>(null);

  /**
   * Bốn nút dùng chung một mutation, nên `isPending` một mình làm **cả bốn cùng quay** — bấm
   * "Duyệt" thì "Từ chối bài" cũng báo đang chạy. `ten` là nhãn để chỉ đúng nút vừa bấm mới quay.
   */
  const lam = useMutation({
    mutationFn: (v: { ten: string; chay: () => Promise<KetQuaDuyet> }) => v.chay(),
    onError: (loi: Error) => toast.loi("Không thực hiện được", loi.message),
    onSuccess: (kq) => {
      datKetQua(kq);
      datLyDo("");
      datThe([]);
      void qc.invalidateQueries({ queryKey: khoa.hangCho });
      void qc.invalidateQueries({ queryKey: khoa.tongQuan });
      // Chính bài đang mở: sau khi duyệt/từ chối thì trạng thái của nó đổi, và trang này phải
      // hiện trạng thái MỚI chứ không phải cái vừa được thay.
      void qc.invalidateQueries({ queryKey: khoa.motBai(id) });
    },
  });

  const lyDoSach = lyDo.trim();
  const lyDoDu = lyDoSach.length >= DAI_LY_DO_TOI_THIEU;

  // `không tìm thấy` và `không tải được` là HAI trạng thái, không phải một. Bài bị xoá thật thì
  // không có gì để thử lại; mạng hỏng thì có. Gộp chúng làm người đọc thử lại một thứ đã mất, hoặc
  // bỏ cuộc trên một thứ chỉ cần bấm lại.
  if (error) {
    const khongCo = error instanceof LoiApi && error.ma === 404;
    return (
      <div className="min-w-0 space-y-4">
        <DauTrang tieuDe="Chi tiết bài" />
        {ketQua !== null && <KetQuaBox kq={ketQua} />}
        {khongCo ? (
          <Rong tieuDe="Không tìm thấy bài này">
            Id <code className="font-mono text-2xs">{id}</code> không có trong database. Có thể nó
            đã bị xoá, hoặc đường dẫn gõ sai.{" "}
            <Link to="/hang-cho" className="underline">
              Về hàng chờ
            </Link>
          </Rong>
        ) : (
          <Bao muc="loi" tieuDe="Không tải được">
            {error.message}
          </Bao>
        )}
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Chi tiết bài" />
        <DangTai />
      </div>
    );
  }

  const nhan = nhanTrangThai(b.trangThai);

  return (
    <div className="min-w-0 space-y-5">
      <DauTrang
        tieuDe="Chi tiết bài"
        trangThai={<Chip muc={nhan.muc} title={nhan.giaiThich}>{nhan.chu}</Chip>}
        moTa={
          <>
            Site <strong>{b.siteSlug}</strong> · slug <code className="text-2xs">/{b.slug}</code>
          </>
        }
      >
        <Link to="/hang-cho" className="text-sm text-muc-500 underline hover:text-muc-800">
          ← Hàng chờ
        </Link>
      </DauTrang>

      {/* Bài mẫu phải TỰ KHAI trước khi người đọc kịp coi nó là bài thật — B8. */}
      {b.laDuLieuMau && (
        <Bao muc="thuong" tieuDe="Đây là DỮ LIỆU MẪU, không phải bài thật">
          Do lệnh <code className="text-2xs">seed-mau</code> tạo để nhìn thử giao diện. Trạng thái,
          lý do từ chối và giờ đăng của nó đều là bịa — đừng đọc chúng như một sự cố.
        </Bao>
      )}

      <The>
        <h2 className="min-w-0 text-lg font-semibold break-words text-muc-900">{b.title.vi}</h2>
        {b.title.en !== undefined && (
          <p className="mt-1 break-words text-sm text-muc-500">EN: {b.title.en}</p>
        )}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {b.tuKhoaMucTieu.map((t) => (
            <Chip key={t} muc="thuong">
              {t}
            </Chip>
          ))}
          {b.modelVietBai !== null && <Chip muc="thuong">{b.modelVietBai}</Chip>}
        </div>
      </The>

      <The>
        <DauThe
          tieuDe="Nội dung — HTML thô, cố ý"
          phu="Render đẹp thì Markdown lọt trông như chữ thường và không ai thấy (AP-88)"
        />
        <pre className="max-h-96 overflow-auto rounded-tile bg-muc-100 p-3 text-2xs leading-relaxed text-muc-700">
          {b.contentHtml.vi}
        </pre>
        {b.contentHtml.en !== undefined && (
          <pre className="mt-2 max-h-96 overflow-auto rounded-tile bg-muc-100 p-3 text-2xs leading-relaxed text-muc-700">
            {b.contentHtml.en}
          </pre>
        )}
      </The>

      {b.captionFacebook !== null && (
        <The>
          <DauThe tieuDe="Caption Facebook" phu="Link về web nằm ở comment đầu, không trong caption (AP-17)" />
          <p className="break-words text-sm text-muc-700">{b.captionFacebook}</p>
        </The>
      )}

      {b.lyDoTuChoi !== null && (
        <Bao muc="loi" tieuDe="Lý do từ chối lần trước">
          {b.lyDoTuChoi}
          {b.theTuChoi.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {b.theTuChoi.map((t) => (
                <Chip key={t} muc="loi">
                  {t}
                </Chip>
              ))}
            </div>
          )}
        </Bao>
      )}

      {/* ── Quyết ─────────────────────────────────────────────────────── */}
      <The className="scroll-mt-4" id="quyet">
        <DauThe tieuDe="Quyết" phu="Lý do đi thẳng vào vòng học và không bổ sung lại được" />

        <Truong
          nhan="Lý do (bắt buộc khi từ chối)"
          goiY={`${lyDoSach.length}/${DAI_LY_DO_TOI_THIEU} ký tự tối thiểu`}
          loi={
            lyDoSach.length > 0 && !lyDoDu
              ? '"không hay" không nói được gì cho bài sau'
              : undefined
          }
        >
          <textarea
            value={lyDo}
            onChange={(e) => datLyDo(e.target.value)}
            rows={3}
            placeholder="Vì sao bài này chưa dùng được?"
            className={lopO}
          />
        </Truong>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {THE_GOI_Y.map((t) => {
            const chon = the.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => datThe((cu) => (chon ? cu.filter((x) => x !== t) : [...cu, t]))}
                className={
                  chon
                    ? "rounded-btn bg-canh-500/12 px-2 py-1 text-2xs font-semibold text-canh-600 ring-1 ring-canh-200"
                    : "rounded-btn bg-muc-0 px-2 py-1 text-2xs text-muc-600 ring-1 ring-muc-300 hover:bg-muc-100"
                }
              >
                {t}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Nut
            kieu="chinh"
            dangChay={lam.isPending && lam.variables?.ten === "duyet"}
            onClick={() => lam.mutate({ ten: "duyet", chay: () => api.duyet(b._id) })}
          >
            Duyệt
          </Nut>
          <Nut
            kieu="nguyHiem"
            dangChay={lam.isPending && lam.variables?.ten === "tu-choi-bai"}
            disabled={!lyDoDu}
            onClick={() =>
              lam.mutate({ ten: "tu-choi-bai", chay: () => api.tuChoiBai(b._id, lyDoSach, the) })
            }
          >
            Từ chối bài
          </Nut>
          <Nut
            kieu="nguyHiem"
            dangChay={lam.isPending && lam.variables?.ten === "tu-choi-anh"}
            disabled={!lyDoDu}
            onClick={() =>
              lam.mutate({ ten: "tu-choi-anh", chay: () => api.tuChoiAnh(b._id, lyDoSach, the) })
            }
          >
            Từ chối ảnh
          </Nut>
          <Nut
            kieu="vien"
            dangChay={lam.isPending && lam.variables?.ten === "bo"}
            onClick={() => lam.mutate({ ten: "bo", chay: () => api.bo(b._id, lyDoSach) })}
          >
            Bỏ bài
          </Nut>
        </div>

        {/*
          Kết quả nằm NGAY DƯỚI hàng nút, không ở cuối trang.
          Đợt 20 — Lynust: *"bấm rồi không biết đã xong chưa"*. Bản trước đặt khối này ở cuối một
          trang dài, tức là ngoài tầm nhìn đúng lúc vừa bấm. Nó không đi bằng toast vì nó **cần
          được đọc**: dòng "bài vẫn nằm trong Hàng chờ" là thứ ngăn Lynust kết luận nút hỏng, và
          một toast tự tắt sau bốn giây thì không đảm bảo được điều đó.
        */}
        {ketQua !== null && (
          <div className="mt-3">
            <KetQuaBox kq={ketQua} />
          </div>
        )}

        <ViSao nhan="ba nút này khác nhau thế nào">
          Từ chối <strong>ảnh</strong> chỉ vẽ lại ảnh — bài giữ nguyên. Từ chối <strong>bài</strong>{" "}
          viết lại từ đầu, và tốn thêm một lượt gọi model. Trần là 2 lượt (AP-97).
        </ViSao>
      </The>

      {/* Lỗi đi bằng toast; kết quả thành công nằm ngay dưới hàng nút ở trên. */}

      {/*
        Trên điện thoại, khối Quyết nằm sau một thân bài HTML thô cuộn cả màn hình — muốn duyệt
        phải cuộn qua toàn bộ bài, mỗi lần. Thanh này giữ *Duyệt* trong tầm ngón cái.

        **Chỉ Duyệt.** Hai nút từ chối cần ô lý do ≥10 ký tự ở khối trên; tách chúng khỏi ô đó là
        hoặc mở một hộp thoại (màn Chi tiết thu nhỏ), hoặc cho từ chối không lý do — và cái thứ
        hai làm hỏng vòng học trong im lặng.
      */}
      {b.trangThai === "PENDING_REVIEW" && (
        <ThanhHanhDong banSaoChoDienThoai>
          <Nut
            kieu="chinh"
            className="flex-1"
            dangChay={lam.isPending && lam.variables?.ten === "duyet"}
            onClick={() => lam.mutate({ ten: "duyet", chay: () => api.duyet(b._id) })}
          >
            Duyệt
          </Nut>
          <a
            href="#quyet"
            className="rounded-btn px-3 py-2 text-sm font-medium text-muc-600 ring-1 ring-muc-300"
          >
            Từ chối…
          </a>
        </ThanhHanhDong>
      )}
    </div>
  );
}

/**
 * Kết quả một thao tác duyệt.
 *
 * Dòng **"bài vẫn nằm trong Hàng chờ"** là dòng quan trọng nhất ở đây, và nó có mặt vì một hiểu
 * nhầm thật: từ chối xong, bài chuyển sang `REJECTED_TEXT`/`REJECTED_IMAGE` — mà hai trạng thái
 * đó **vẫn thuộc hàng chờ** (cố ý: chúng cần được theo dõi). Người bấm thấy bài còn nguyên tại
 * chỗ cũ và kết luận **nút không có tác dụng**, trong khi API đã trả 200 và job viết lại đã được
 * xếp. Một thao tác chạy đúng mà nhìn như không chạy thì cũng tệ ngang một thao tác hỏng.
 */
function KetQuaBox({ kq }: { kq: KetQuaDuyet }) {
  const laTuChoi = kq.trangThaiMoi === "REJECTED_TEXT" || kq.trangThaiMoi === "REJECTED_IMAGE";

  return (
    <Bao muc="on" tieuDe={`Đã chuyển sang ${kq.trangThaiMoi}`}>
      <ul className="mt-1 space-y-1">
        {laTuChoi && (
          <li>
            <strong>Bài vẫn nằm trong Hàng chờ</strong> — đó là đúng, không phải nút chưa chạy.
            Trạng thái đã đổi và nó ở lại để anh theo dõi cho tới khi bản viết lại xong.
          </li>
        )}
        <li>
          Huỷ <strong>{kq.soJobDaHuy}</strong> job đang chờ — không huỷ thì bài vừa từ chối vẫn lên
          tài khoản thật (AP-95)
        </li>
        <li>
          Job tiếp theo:{" "}
          {kq.jobTiepTheo !== null ? (
            <>
              <code className="text-2xs">{kq.jobTiepTheo}</code>
              <span className="text-2xs text-on-600">
                {" "}
                — job chạy nền; nếu cron đang tắt thì bấm &ldquo;Chạy một lượt&rdquo; ở Tổng quan
              </span>
            </>
          ) : (
            <span className="text-on-600">không có</span>
          )}
        </li>
      </ul>
      {kq.canhBao.map((c, i) => (
        <p key={i} className="mt-2 text-canh-700">
          ⚠️ {c}
        </p>
      ))}
    </Bao>
  );
}
